import { useState, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ThemeToggle } from '@/components/ThemeToggle';
import {
  FileText, ArrowLeft, Upload, Loader2, Briefcase, MapPin, ExternalLink, X,
  Sparkles, Award, Target, TrendingUp, IndianRupee, Zap, Globe, Bookmark,
  Plus, Wand2, Shield, Lock, ChevronRight, BarChart3, Activity, Lightbulb,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const JOB_TYPES = ['Full-time', 'Internship', 'Part-time', 'Remote', 'Contract'];

const TRUSTED_LOGOS = ['Google', 'Microsoft', 'Razorpay', 'Flipkart', 'Stripe', 'TCS'];

const TRENDING_SKILLS_BY_DOMAIN = {
  ai: ['LangChain', 'Vector DBs', 'MLOps', 'PyTorch', 'Hugging Face'],
  web: ['Next.js', 'TypeScript', 'tRPC', 'Tailwind', 'Vercel'],
  data: ['dbt', 'Snowflake', 'Airflow', 'Spark', 'BigQuery'],
  default: ['Cloud Architecture', 'Kubernetes', 'System Design', 'GraphQL'],
};

// ============== UTILITIES ==============
const fmtSalary = (min, max) => {
  if (!min && !max) return null;
  const f = (n) => (n >= 100000 ? `${(n / 100000).toFixed(1).replace(/\.0$/, '')}L` : n >= 1000 ? `${Math.round(n / 1000)}K` : `${n}`);
  if (min && max && min !== max) return `${f(min)} – ${f(max)}`;
  return f(min || max);
};

const pickDomain = (skills = []) => {
  const s = skills.map((x) => (x || '').toLowerCase()).join(' ');
  if (/(ai|llm|ml|gpt|rag|pytorch|tensorflow)/.test(s)) return 'ai';
  if (/(react|next|node|frontend|backend|web)/.test(s)) return 'web';
  if (/(data|sql|etl|airflow|snowflake)/.test(s)) return 'data';
  return 'default';
};

const computeInsights = (profile, jobs, tiered) => {
  const avgAts = jobs.length ? Math.round(jobs.reduce((s, j) => s + (j.ats_score || 0), 0) / jobs.length) : 0;
  const employability = Math.min(100, avgAts + (tiered.tier_1?.length || 0) * 4 + (tiered.tier_2?.length || 0) * 2);

  const aiKeywords = ['ai', 'llm', 'ml', 'gpt', 'python', 'rag', 'genai'];
  const matchedAi = (profile.skills || []).some((s) => aiKeywords.includes((s || '').toLowerCase()));
  const marketDemand = matchedAi ? 'Very High' : avgAts > 50 ? 'High' : 'Stable';

  const remoteCount = jobs.filter((j) => /remote|hybrid|work from home/i.test(`${j.location} ${j.employment_type} ${j.description}`)).length;
  const remoteReadiness = jobs.length ? Math.round((remoteCount / jobs.length) * 100) : 60;

  const allSalaries = jobs.flatMap((j) => [j.salary_min, j.salary_max]).filter(Boolean);
  const salaryEstimate = allSalaries.length
    ? `${fmtSalary(Math.min(...allSalaries), Math.max(...allSalaries))}`
    : profile.experience_years_num >= 5
      ? '18L – 35L'
      : profile.experience_years_num >= 2
        ? '8L – 18L'
        : '4L – 9L';

  const domain = pickDomain(profile.skills);
  const trending = TRENDING_SKILLS_BY_DOMAIN[domain] || TRENDING_SKILLS_BY_DOMAIN.default;
  const ownLower = new Set((profile.skills || []).map((s) => (s || '').toLowerCase()));
  const missingSkills = trending.filter((t) => !ownLower.has(t.toLowerCase())).slice(0, 4);

  return { employability, marketDemand, remoteReadiness, salaryEstimate, missingSkills, avgAts };
};

const computeMissingForJob = (job, profile) => {
  const jobText = `${job.title} ${job.description}`.toLowerCase();
  const profSkills = new Set((profile.skills || []).map((s) => (s || '').toLowerCase()));
  const domain = pickDomain(profile.skills);
  const trending = TRENDING_SKILLS_BY_DOMAIN[domain] || TRENDING_SKILLS_BY_DOMAIN.default;
  return trending.filter((t) => jobText.includes(t.toLowerCase()) && !profSkills.has(t.toLowerCase())).slice(0, 3);
};

// ============== HERO + UPLOAD ==============
function Hero({ onResult }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const [location, setLocation] = useState('');
  const [jobType, setJobType] = useState('Full-time');
  const [loading, setLoading] = useState(false);

  const handleFile = useCallback((f) => {
    if (!f) return;
    if (!['application/pdf', 'text/plain'].includes(f.type) && !f.name.match(/\.(pdf|txt)$/i)) {
      toast.error('Please upload a PDF or TXT file');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error('File too large — max 5MB');
      return;
    }
    setFile(f);
  }, []);

  const search = async () => {
    if (!file) return;
    setLoading(true);
    const form = new FormData();
    form.append('file', file);
    form.append('location', location || 'India');
    form.append('job_type', jobType);
    try {
      const { data } = await axios.post(`${API_URL}/api/jobs/search`, form, { withCredentials: true });
      onResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Search failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <section className="relative overflow-hidden">
      {/* Glow background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[640px] h-[640px] bg-orange-500/10 dark:bg-orange-500/20 blur-3xl rounded-full" />
        <div className="absolute top-40 right-10 w-[300px] h-[300px] bg-amber-400/10 dark:bg-amber-400/15 blur-3xl rounded-full" />
      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-12 pt-12 pb-6 text-center">
        <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-orange-600 dark:text-orange-300 bg-orange-100/80 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/30 px-3 py-1.5 rounded-full backdrop-blur">
          <Sparkles className="w-3.5 h-3.5" /> AI Career Copilot · Live
        </span>
        <h1
          className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05] text-slate-900 dark:text-white"
          style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
          data-testid="job-search-heading"
        >
          AI Finds Jobs That <span className="bg-gradient-to-r from-orange-500 via-amber-400 to-orange-500 bg-clip-text text-transparent">Actually Match</span> Your Skills
        </h1>
        <p className="mt-5 max-w-2xl mx-auto text-base sm:text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
          Upload your resume and Aarova AI instantly discovers high-quality opportunities tailored to your experience, skills, salary goals, and career path.
        </p>

        {/* Live stats */}
        <div className="mt-6 flex flex-wrap justify-center gap-x-8 gap-y-3 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
          <span className="flex items-center gap-1.5"><Activity className="w-3.5 h-3.5 text-emerald-500" /> <b className="text-slate-700 dark:text-slate-200 font-bold">240k+</b> live roles indexed</span>
          <span className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5 text-sky-500" /> <b className="text-slate-700 dark:text-slate-200 font-bold">42</b> countries</span>
          <span className="flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5 text-orange-500" /> <b className="text-slate-700 dark:text-slate-200 font-bold">68k</b> remote roles</span>
        </div>
      </div>

      {/* Upload card */}
      <div className="max-w-3xl mx-auto px-6 md:px-12 pb-12">
        <div className="relative">
          <div className="absolute -inset-px bg-gradient-to-r from-orange-500/30 via-amber-400/20 to-orange-500/30 rounded-2xl blur-md opacity-60 dark:opacity-100" />
          <div className="relative bg-white/90 dark:bg-slate-900/80 backdrop-blur-xl border border-slate-200 dark:border-slate-800 rounded-2xl p-5 sm:p-7 shadow-xl">
            {loading ? (
              <AIScanLoader />
            ) : (
              <>
                <div
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                  onDragLeave={() => setDrag(false)}
                  onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files[0]); }}
                  className={`relative cursor-pointer rounded-xl border-2 border-dashed p-8 sm:p-10 text-center transition-all overflow-hidden group ${drag ? 'border-orange-500 bg-orange-50 dark:bg-orange-500/10' : 'border-slate-300 dark:border-slate-700 hover:border-orange-500 hover:bg-orange-50/50 dark:hover:bg-orange-500/5'}`}
                  data-testid="job-resume-dropzone"
                >
                  <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-500/50 to-transparent" />
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-500/20 dark:to-amber-500/20 mb-3 group-hover:scale-110 transition-transform">
                    <Upload className="w-6 h-6 text-orange-600 dark:text-orange-400" strokeWidth={2} />
                  </div>
                  <p className="font-bold text-slate-900 dark:text-white mb-1">Drop your resume here</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">PDF or TXT · max 5 MB · AI will analyze in seconds</p>
                </div>
                <input ref={fileRef} type="file" accept=".pdf,.txt" className="hidden" onChange={(e) => handleFile(e.target.files[0])} />

                {file && (
                  <div data-testid="job-selected-file" className="mt-4 flex items-center gap-3 bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/30 px-4 py-2.5 rounded-lg">
                    <FileText className="w-4 h-4 text-orange-600 dark:text-orange-300 flex-shrink-0" />
                    <span className="flex-1 text-sm font-semibold text-slate-900 dark:text-orange-100 truncate">{file.name}</span>
                    <button onClick={() => setFile(null)} className="text-slate-500 hover:text-slate-900 dark:hover:text-white" data-testid="clear-file-btn">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}

                <div className="grid sm:grid-cols-2 gap-3 mt-4">
                  <Input data-testid="job-location-input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Location (e.g. Hyderabad, India)" className="h-11 bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 rounded-lg focus-visible:ring-1 focus-visible:ring-orange-500 focus-visible:border-orange-500" />
                  <select data-testid="job-type-select" value={jobType} onChange={(e) => setJobType(e.target.value)} className="h-11 px-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white text-sm rounded-lg focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500">
                    {JOB_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>

                <Button
                  data-testid="job-search-submit-btn"
                  disabled={!file}
                  onClick={search}
                  className="w-full mt-4 h-12 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 disabled:from-slate-300 disabled:to-slate-300 disabled:dark:from-slate-700 disabled:dark:to-slate-700 disabled:cursor-not-allowed text-white font-bold rounded-lg text-base shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all active:scale-[0.99]"
                >
                  <Wand2 className="w-4 h-4 mr-2" />
                  {file ? 'Run AI Match — Find Best Jobs' : 'Upload a resume to begin'}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Trust strip */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 opacity-70">
          <span className="text-[11px] uppercase tracking-widest text-slate-400 dark:text-slate-500 font-semibold">Sourcing from</span>
          {TRUSTED_LOGOS.map((c) => (
            <span key={c} className="text-sm font-bold text-slate-400 dark:text-slate-500" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{c}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

function AIScanLoader() {
  return (
    <div data-testid="job-search-loading" className="py-10 text-center relative overflow-hidden rounded-xl">
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-orange-200/40 dark:via-orange-500/10 to-transparent animate-[shimmer_2s_infinite]"
           style={{ backgroundSize: '200% 100%' }} />
      <style>{`@keyframes shimmer { 0% { background-position: -200% 0 } 100% { background-position: 200% 0 } }`}</style>
      <div className="relative inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 mb-4 shadow-lg shadow-orange-500/40">
        <Loader2 className="w-7 h-7 animate-spin text-white" />
      </div>
      <h3 className="text-xl font-black text-slate-900 dark:text-white mb-1" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>AI is scanning your resume…</h3>
      <p className="text-sm text-slate-500 dark:text-slate-400">Extracting skills · Scoring 240k+ jobs · Ranking by tier</p>
      <div className="mt-5 max-w-sm mx-auto space-y-2">
        {['Parsing experience & skills', 'Querying LinkedIn · JSearch · Adzuna', 'Computing ATS match scores'].map((s, i) => (
          <div key={s} className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
            <span className="w-1.5 h-1.5 rounded-full bg-orange-500 animate-pulse" style={{ animationDelay: `${i * 200}ms` }} />
            <span>{s}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============== TIER THEMES ==============
const TIER_THEMES = {
  1: { label: 'Tier 1', sub: 'Exact Match', ring: 'stroke-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', chip: 'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30', border: 'border-emerald-500/40', dot: 'bg-emerald-500' },
  2: { label: 'Tier 2', sub: 'Strong Match', ring: 'stroke-sky-500', text: 'text-sky-600 dark:text-sky-400', chip: 'bg-sky-100 dark:bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-500/30', border: 'border-sky-500/40', dot: 'bg-sky-500' },
  3: { label: 'Tier 3', sub: 'Good Fit', ring: 'stroke-slate-400', text: 'text-slate-600 dark:text-slate-400', chip: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700', border: 'border-slate-300 dark:border-slate-700', dot: 'bg-slate-400' },
};

// ============== CIRCULAR MATCH SCORE ==============
function MatchRing({ score, tier }) {
  const radius = 24;
  const c = 2 * Math.PI * radius;
  const offset = c - (score / 100) * c;
  const theme = TIER_THEMES[tier] || TIER_THEMES[3];
  return (
    <div className="relative w-16 h-16 flex-shrink-0" data-testid={`match-ring-${score}`}>
      <svg className="w-16 h-16 -rotate-90" viewBox="0 0 60 60">
        <circle cx="30" cy="30" r={radius} className="fill-none stroke-slate-200 dark:stroke-slate-800" strokeWidth="5" />
        <circle
          cx="30" cy="30" r={radius}
          className={`fill-none ${theme.ring} transition-all duration-700`}
          strokeWidth="5"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-base font-black leading-none ${theme.text}`} style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{score}</span>
        <span className="text-[8px] font-bold tracking-wider text-slate-500 dark:text-slate-500 uppercase mt-0.5">Match</span>
      </div>
    </div>
  );
}

// ============== JOB CARD ==============
function JobCard({ job, tier, profile, savedSet, onToggleSave }) {
  const theme = TIER_THEMES[tier] || TIER_THEMES[3];
  const salary = fmtSalary(job.salary_min, job.salary_max);
  const isRemote = /remote|work from home/i.test(`${job.location} ${job.employment_type} ${job.description}`);
  const missing = useMemo(() => computeMissingForJob(job, profile), [job, profile]);
  const id = `${job.title}-${job.company}-${job.rank}`;
  const saved = savedSet.has(id);
  const isHot = (job.ats_score || 0) >= 75;

  return (
    <article
      data-testid={`job-card-${job.rank}`}
      className={`group relative bg-white dark:bg-slate-900/80 border ${theme.border} hover:border-orange-500/60 rounded-xl p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_40px_-10px_rgba(249,115,22,0.25)]`}
    >
      {/* Top row: logo + score */}
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center overflow-hidden">
          {job.logo ? (
            <img src={job.logo} alt={job.company} className="w-full h-full object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
          ) : (
            <span className="text-base font-black text-slate-500" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {(job.company || '?').slice(0, 1).toUpperCase()}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <a href={job.apply_url} target="_blank" rel="noopener noreferrer" className="block">
            <h3 className="font-bold text-slate-900 dark:text-white text-[15px] leading-snug group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors line-clamp-2">
              {job.title}
            </h3>
          </a>
          <p className="text-sm text-slate-600 dark:text-slate-400 truncate">{job.company}</p>
        </div>
        <MatchRing score={job.ats_score || 0} tier={tier} />
      </div>

      {/* Meta row */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {salary && (
          <span data-testid={`job-salary-${job.rank}`} className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-md bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/30">
            <IndianRupee className="w-3 h-3" /> {salary}
          </span>
        )}
        {isRemote && (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-md bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-300 border border-sky-200 dark:border-sky-500/30">
            <Globe className="w-3 h-3" /> Remote
          </span>
        )}
        {isHot && (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-md bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-500/30">
            <Zap className="w-3 h-3" /> Hiring Fast
          </span>
        )}
        <span className="text-[11px] text-slate-500 dark:text-slate-500 flex items-center gap-1">
          <MapPin className="w-3 h-3" /> {job.location || '—'}
        </span>
        {job.posted && <span className="text-[11px] text-slate-500 dark:text-slate-500">· {job.posted}</span>}
      </div>

      {/* Skills overlap visualization */}
      {(job.matching_skills?.length > 0 || missing.length > 0) && (
        <div className="mb-3 space-y-1.5">
          {job.matching_skills?.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 mr-1">Matched</span>
              {job.matching_skills.slice(0, 5).map((s) => (
                <span key={s} className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 capitalize">{s}</span>
              ))}
            </div>
          )}
          {missing.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 mr-1">Add</span>
              {missing.map((s) => (
                <span key={s} className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-dashed border-amber-300/50 dark:border-amber-500/30">{s}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {job.description && (
        <p className="text-xs text-slate-600 dark:text-slate-400 line-clamp-2 leading-relaxed mb-4">{job.description}</p>
      )}

      {/* Footer actions */}
      <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider ${theme.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${theme.dot}`} /> {theme.label} · {theme.sub}
          </span>
          <span className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-600 hidden sm:inline">
            via {job.source === 'jsearch' ? 'JSearch' : job.source === 'adzuna' ? 'Adzuna' : job.source === 'linkedin' ? 'LinkedIn' : 'Live'}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            data-testid={`save-job-${job.rank}`}
            onClick={() => onToggleSave(id)}
            className={`h-8 w-8 inline-flex items-center justify-center rounded-md border transition-colors ${saved ? 'bg-orange-50 dark:bg-orange-500/10 border-orange-200 dark:border-orange-500/30 text-orange-600 dark:text-orange-300' : 'border-slate-200 dark:border-slate-700 text-slate-500 hover:text-orange-600 hover:border-orange-300'}`}
            aria-label="Save job"
          >
            <Bookmark className={`w-3.5 h-3.5 ${saved ? 'fill-current' : ''}`} />
          </button>
          <a
            href={job.apply_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`apply-btn-${job.rank}`}
            className="inline-flex items-center gap-1 h-8 px-3 rounded-md bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-xs font-bold hover:bg-orange-500 dark:hover:bg-orange-500 dark:hover:text-white transition-colors"
          >
            Apply <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </article>
  );
}

// ============== TIER SECTION ==============
function TierSection({ tier, jobs, profile, savedSet, onToggleSave }) {
  if (!jobs?.length) return null;
  const theme = TIER_THEMES[tier];
  return (
    <section data-testid={`tier-section-${tier}`} className="space-y-4">
      <div className="flex items-center gap-3">
        <span className={`w-2 h-2 rounded-full ${theme.dot}`} />
        <h2 className={`text-lg font-black tracking-tight ${theme.text}`} style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          {theme.label}
        </h2>
        <span className="text-sm font-bold text-slate-400 dark:text-slate-600">— {theme.sub}</span>
        <span className="ml-auto text-xs font-bold text-slate-500 dark:text-slate-500 bg-slate-100 dark:bg-slate-800/60 px-2 py-0.5 rounded-full">{jobs.length}</span>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {jobs.map((j) => <JobCard key={`${j.title}-${j.company}-${j.rank}`} job={j} tier={tier} profile={profile} savedSet={savedSet} onToggleSave={onToggleSave} />)}
      </div>
    </section>
  );
}

// ============== AI PROFILE CARD (main feed top) ==============
function ProfileHero({ profile, insights, tiered, jobs }) {
  return (
    <div className="relative overflow-hidden bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-2xl p-6">
      <div className="absolute -top-20 -right-20 w-64 h-64 bg-orange-500/10 dark:bg-orange-500/15 rounded-full blur-3xl" />
      <div className="relative flex items-start gap-4">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-xl font-black text-white shadow-lg shadow-orange-500/30" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          {profile.initials || (profile.name || '?').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h2 className="text-xl font-black text-slate-900 dark:text-white truncate" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {profile.name || 'Candidate'}
            </h2>
            {insights.employability >= 80 && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white">
                <Award className="w-3 h-3" /> Top 10% Match
              </span>
            )}
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-400">{profile.title} · {profile.experience_years}</p>
          {profile.education && <p className="text-xs text-slate-500 dark:text-slate-500 mt-0.5">{profile.education}</p>}
        </div>
      </div>

      {/* Stats strip */}
      <div className="mt-5 pt-5 border-t border-slate-100 dark:border-slate-800 grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Stat label="Total Matches" value={jobs.length} sub="AI ranked" />
        <Stat label="Avg ATS" value={`${insights.avgAts}%`} sub="vs market" accent="text-orange-600 dark:text-orange-400" />
        <Stat label="Tier 1" value={tiered.tier_1?.length || 0} sub="Exact match" accent="text-emerald-600 dark:text-emerald-400" />
        <Stat label="Tier 2" value={tiered.tier_2?.length || 0} sub="Strong match" accent="text-sky-600 dark:text-sky-400" />
      </div>
    </div>
  );
}

function Stat({ label, value, sub, accent }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-500">{label}</p>
      <p className={`text-2xl font-black ${accent || 'text-slate-900 dark:text-white'}`} style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{value}</p>
      {sub && <p className="text-[10px] text-slate-400 dark:text-slate-600 mt-0.5">{sub}</p>}
    </div>
  );
}

// ============== KEY DIFFERENTIATORS ==============
function KeyDifferentiators({ items }) {
  if (!items?.length) return null;
  return (
    <div data-testid="key-differentiators-section" className="bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg bg-orange-100 dark:bg-orange-500/15 flex items-center justify-center">
          <Award className="w-4 h-4 text-orange-600 dark:text-orange-400" />
        </div>
        <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-wider" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Your Key Differentiators
        </h3>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {items.slice(0, 8).map((d, i) => (
          <div key={`${d}-${i}`} data-testid={`differentiator-${i}`} className="flex items-start gap-2 text-xs">
            <Sparkles className="w-3 h-3 text-orange-500 flex-shrink-0 mt-0.5" />
            <span className="font-medium text-slate-700 dark:text-slate-300 leading-snug">{d}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============== SMART FILTERS ==============
function SmartFilters({ active, onToggle, available }) {
  return (
    <div data-testid="smart-filters" className="flex flex-wrap items-center gap-2">
      <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-orange-600 dark:text-orange-400 mr-1 flex items-center gap-1">
        <Sparkles className="w-3 h-3" /> AI Filters
      </span>
      {available.map((f) => {
        const isOn = active.includes(f.id);
        return (
          <button
            key={f.id}
            data-testid={`filter-${f.id}`}
            onClick={() => onToggle(f.id)}
            className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full transition-all ${isOn ? 'bg-slate-900 dark:bg-orange-500 text-white border border-slate-900 dark:border-orange-500 shadow-sm' : 'bg-white dark:bg-slate-900/60 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-orange-400 hover:text-orange-600 dark:hover:text-orange-400'}`}
          >
            {f.icon && <f.icon className="w-3 h-3" />}
            {f.label}
            {isOn && <X className="w-3 h-3" />}
          </button>
        );
      })}
    </div>
  );
}

// ============== AI INSIGHTS SIDEBAR ==============
function GaugeBar({ label, value, color = 'bg-orange-500' }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300">{label}</span>
        <span className="text-[11px] font-black text-slate-900 dark:text-white">{value}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-700`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

function InsightsSidebar({ insights, profile, navigate }) {
  return (
    <aside data-testid="ai-insights-sidebar" className="space-y-4 lg:sticky lg:top-24">
      {/* Employability */}
      <div className="bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 relative overflow-hidden">
        <div className="absolute -top-12 -right-12 w-40 h-40 bg-orange-500/10 rounded-full blur-2xl" />
        <div className="relative">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-500 mb-2 flex items-center gap-1"><BarChart3 className="w-3 h-3" /> AI Employability</p>
          <div className="flex items-baseline gap-2 mb-3">
            <span className="text-5xl font-black bg-gradient-to-br from-orange-500 to-amber-500 bg-clip-text text-transparent" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{insights.employability}</span>
            <span className="text-sm font-bold text-slate-500 dark:text-slate-400">/100</span>
          </div>
          <div className="space-y-3">
            <GaugeBar label="ATS Strength" value={insights.avgAts} color="bg-emerald-500" />
            <GaugeBar label="Remote Readiness" value={insights.remoteReadiness} color="bg-sky-500" />
            <GaugeBar label="Market Demand" value={insights.marketDemand === 'Very High' ? 92 : insights.marketDemand === 'High' ? 75 : 55} color="bg-orange-500" />
          </div>
        </div>
      </div>

      {/* Salary estimate */}
      <div className="bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-500 mb-1 flex items-center gap-1"><IndianRupee className="w-3 h-3" /> Salary Estimate</p>
        <p className="text-2xl font-black text-slate-900 dark:text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{insights.salaryEstimate}</p>
        <p className="text-[11px] text-slate-500 dark:text-slate-500 mt-1">Based on your matches · annualized</p>
      </div>

      {/* Skill gaps */}
      {insights.missingSkills.length > 0 && (
        <div className="bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-500 mb-3 flex items-center gap-1"><Lightbulb className="w-3 h-3" /> Trending Skills To Add</p>
          <div className="space-y-2">
            {insights.missingSkills.map((s) => (
              <div key={s} className="flex items-center justify-between text-xs">
                <span className="font-semibold text-slate-700 dark:text-slate-300">{s}</span>
                <span className="text-amber-600 dark:text-amber-400 font-bold flex items-center gap-0.5"><Plus className="w-3 h-3" /> Add</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Premium upgrade */}
      <div className="relative overflow-hidden rounded-2xl p-5 bg-gradient-to-br from-slate-900 to-slate-800 dark:from-slate-950 dark:to-slate-900 text-white border border-orange-500/30">
        <div className="absolute -top-4 -right-4 w-32 h-32 bg-orange-500/30 rounded-full blur-3xl" />
        <div className="relative">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-orange-500 text-slate-950 mb-3">
            <Lock className="w-3 h-3" /> Premium
          </span>
          <h4 className="text-lg font-black leading-tight mb-1" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Unlock AI Job Matching</h4>
          <p className="text-xs text-slate-400 leading-relaxed mb-4">Unlimited AI tailoring · Recruiter visibility · AI interview prep · Cover letters</p>
          <button
            data-testid="premium-upgrade-btn"
            onClick={() => navigate('/subscription')}
            className="w-full h-10 inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-slate-950 font-bold text-sm rounded-lg transition-colors"
          >
            Upgrade · ₹299/mo <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Quick actions */}
      <div className="bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-500 mb-3">Quick Actions</p>
        <div className="space-y-2">
          <button onClick={() => navigate('/dashboard')} className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
            <FileText className="w-3.5 h-3.5 text-slate-500" /> Edit Resume
          </button>
          <button onClick={() => navigate('/ats-optimizer')} className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
            <Target className="w-3.5 h-3.5 text-slate-500" /> Improve ATS Score
          </button>
          <button onClick={() => toast.info('Cover letter generation coming soon')} className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors">
            <Wand2 className="w-3.5 h-3.5 text-slate-500" /> Generate Cover Letter
          </button>
        </div>
      </div>
    </aside>
  );
}

// ============== EMPTY STATE ==============
function SmartEmptyState({ profile, insights }) {
  return (
    <div data-testid="smart-empty-state" className="bg-white dark:bg-slate-900/80 border border-dashed border-slate-300 dark:border-slate-700 rounded-2xl p-8 text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-orange-100 dark:bg-orange-500/15 mb-3">
        <Lightbulb className="w-6 h-6 text-orange-600 dark:text-orange-400" />
      </div>
      <h3 className="font-black text-slate-900 dark:text-white mb-1" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>No exact matches — but here's how to unlock more</h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">AI suggests these next steps based on your profile.</p>
      <div className="max-w-md mx-auto text-left space-y-2">
        {insights.missingSkills.map((s) => (
          <div key={s} className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300">
            <span className="w-1.5 h-1.5 rounded-full bg-orange-500" /> Add <b className="font-bold">{s}</b> to your resume
          </div>
        ))}
        <div className="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300">
          <span className="w-1.5 h-1.5 rounded-full bg-orange-500" /> Try broader job titles related to <b>{profile.title}</b>
        </div>
      </div>
    </div>
  );
}

// ============== RESULTS VIEW ==============
function ResultsView({ data, onReset, navigate }) {
  const { profile, tiered = {}, jobs = [] } = data;
  const insights = useMemo(() => computeInsights(profile, jobs, tiered), [profile, jobs, tiered]);
  const [savedSet, setSavedSet] = useState(() => new Set());
  const [activeFilters, setActiveFilters] = useState([]);

  const toggleSave = useCallback((id) => {
    setSavedSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); toast.success('Removed from saved'); }
      else { next.add(id); toast.success('Saved'); }
      return next;
    });
  }, []);

  const toggleFilter = useCallback((id) => {
    setActiveFilters((prev) => prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]);
  }, []);

  const filters = [
    { id: 'remote', label: 'Remote', icon: Globe },
    { id: 'high-salary', label: 'High Salary', icon: IndianRupee },
    { id: 'top-match', label: 'Top Match', icon: Award },
    { id: 'easy-apply', label: 'Easy Apply', icon: Zap },
    { id: 'saved', label: 'Saved', icon: Bookmark },
  ];

  const applyFilters = useCallback((list) => {
    if (!activeFilters.length) return list;
    return list.filter((j) => {
      if (activeFilters.includes('remote') && !/remote|hybrid|work from home/i.test(`${j.location} ${j.employment_type} ${j.description}`)) return false;
      if (activeFilters.includes('high-salary') && !(j.salary_min || j.salary_max)) return false;
      if (activeFilters.includes('top-match') && (j.ats_score || 0) < 75) return false;
      if (activeFilters.includes('easy-apply') && !j.apply_url) return false;
      if (activeFilters.includes('saved') && !savedSet.has(`${j.title}-${j.company}-${j.rank}`)) return false;
      return true;
    });
  }, [activeFilters, savedSet]);

  const t1 = applyFilters(tiered.tier_1 || []);
  const t2 = applyFilters(tiered.tier_2 || []);
  const t3 = applyFilters(tiered.tier_3 || []);
  const totalAfterFilter = t1.length + t2.length + t3.length;

  return (
    <div data-testid="job-results-view" className="grid lg:grid-cols-[1fr_320px] gap-6">
      <div className="space-y-6 min-w-0">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            {totalAfterFilter} curated matches
          </h2>
          <button data-testid="job-results-reset-btn" onClick={onReset} className="text-xs font-bold text-slate-500 dark:text-slate-400 hover:text-orange-600 transition-colors flex items-center gap-1">
            <Upload className="w-3.5 h-3.5" /> New Search
          </button>
        </div>

        <ProfileHero profile={profile} insights={insights} tiered={tiered} jobs={jobs} />

        <KeyDifferentiators items={profile.key_differentiators || profile.differentiators} />

        <SmartFilters active={activeFilters} onToggle={toggleFilter} available={filters} />

        {totalAfterFilter === 0 ? (
          <SmartEmptyState profile={profile} insights={insights} />
        ) : (
          <>
            <TierSection tier={1} jobs={t1} profile={profile} savedSet={savedSet} onToggleSave={toggleSave} />
            <TierSection tier={2} jobs={t2} profile={profile} savedSet={savedSet} onToggleSave={toggleSave} />
            <TierSection tier={3} jobs={t3} profile={profile} savedSet={savedSet} onToggleSave={toggleSave} />
          </>
        )}

        {/* Niche placeholder */}
        <div data-testid="niche-categories-section" className="bg-white/50 dark:bg-slate-900/40 border border-dashed border-slate-300 dark:border-slate-700 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-slate-500" />
            <h4 className="text-xs font-black text-slate-700 dark:text-slate-300 uppercase tracking-wider">Niche Roles · Coming Soon</h4>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">Specialised role types tailored to your unique strengths will appear here.</p>
        </div>
      </div>

      <InsightsSidebar insights={insights} profile={profile} navigate={navigate} />
    </div>
  );
}

// ============== ROOT PAGE ==============
export default function JobSearchPage() {
  const navigate = useNavigate();
  const [result, setResult] = useState(null);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Glass header */}
      <header className="sticky top-0 z-40 bg-white/80 dark:bg-slate-950/80 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-3.5 flex justify-between items-center">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 group">
            <FileText className="w-7 h-7 text-orange-500 group-hover:scale-110 transition-transform" strokeWidth={2} />
            <span className="text-xl font-black text-slate-900 dark:text-white tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Aarova AI</span>
            <span className="hidden sm:inline-flex items-center gap-1 ml-2 text-[10px] font-bold uppercase tracking-widest text-orange-600 dark:text-orange-400 bg-orange-100/60 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/20 px-2 py-0.5 rounded-full">
              <Sparkles className="w-2.5 h-2.5" /> Job Search
            </span>
          </button>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button data-testid="job-back-btn" onClick={() => navigate('/dashboard')} variant="ghost" className="text-xs font-semibold text-slate-700 dark:text-slate-300 hover:text-orange-600">
              <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 pb-20">
        {result
          ? <div className="pt-8"><ResultsView data={result} onReset={() => setResult(null)} navigate={navigate} /></div>
          : <Hero onResult={setResult} />
        }
      </main>
    </div>
  );
}
